import { TravelTimelineItemShiftFragment } from "../../components/RouteDetailPanelTimeline/components/TravelTimelineItem/fragments.generated";
import { VisitTimelineItemFragment } from "../../components/RouteDetailPanelTimeline/components/VisitTimelineItem/fragments.generated";
import { PlannerV2TravelGroupGetTimelineQuery } from "../../queries.generated";
import { LocationMetadata } from "./shiftPickupMetadata";

type TimelineItem = PlannerV2TravelGroupGetTimelineQuery["plannerV2TravelGroupGetTimeline"]["timeline"][number];

type QueryTravelItem = Extract<TimelineItem, { __typename: "GqlPlannerV2TravelGroupTravelTimelineItem" }>;

export type EnrichedShift = TravelTimelineItemShiftFragment & {
    pickupLocation: LocationMetadata | null;
};

type EnrichedTravelItem = Omit<QueryTravelItem, "pickups" | "dropoffs"> & {
    pickups?: EnrichedShift[];
    dropoffs?: EnrichedShift[];
};

export type EnrichedTimelineItem = {
    time: string;
    key: string;
} & (EnrichedTravelItem | VisitTimelineItemFragment);

export function enrichTimelineWithPickupData(
    timeline: TimelineItem[] | undefined,
    locationMetadataByShiftId: Map<string, LocationMetadata>,
): EnrichedTimelineItem[] {
    if (!timeline) return [];

    return timeline.flatMap((item): EnrichedTimelineItem[] => {
        if (item.__typename === "GqlPlannerV2Visit") {
            return [
                {
                    ...item,
                    time: item.visitStartTime as string,
                    key: item.visitId,
                },
            ];
        }

        if (item.__typename === "GqlPlannerV2TravelGroupTravelTimelineItem") {
            return buildTravelTimelineItems(item, locationMetadataByShiftId);
        }

        return [];
    });
}

function buildTravelTimelineItems(
    item: QueryTravelItem,
    locationMetadataByShiftId: Map<string, LocationMetadata>,
): EnrichedTimelineItem[] {
    const enrichShift = (shift: TravelTimelineItemShiftFragment): EnrichedShift => ({
        ...shift,
        pickupLocation: locationMetadataByShiftId.get(shift.id) ?? null,
    });

    const isFirstOutbound = item.direction === "outbound" && item.numberInSequence === 1;
    const isLastInbound = item.direction === "inbound" && item.numberInSequence === item.totalCount;

    const result: EnrichedTimelineItem[] = [];

    if (item.pickups?.length > 0 && !isFirstOutbound) {
        result.push({
            ...item,
            __typename: "GqlPlannerV2TravelGroupTravelTimelineItem",
            time: item.timelineItemStartTime,
            key: `${item.id}-pickup`,
            pickups: item.pickups.map(enrichShift),
            dropoffs: undefined,
        });
    }

    if (item.dropoffs?.length > 0 && !isLastInbound) {
        result.push({
            ...item,
            __typename: "GqlPlannerV2TravelGroupTravelTimelineItem",
            time: item.endTime,
            key: `${item.id}-dropoff`,
            pickups: undefined,
            dropoffs: item.dropoffs.map(enrichShift),
        });
    }

    return result;
}
